// `GET /devices/config` on every sync (plan §9.5, §10.5): the entitlement token, the minimum
// app version, the station's own settings, and the server's clock.
//
// Clock skew: the server time is compared with the midpoint of the request on the station's
// clock; more than two minutes either way → the station warns (the server records
// `received_at` and flags far-future `occurred_at` itself).

import { type DeviceConfigResponse, upgradeNoticeFrom } from '@enp/domain';
import type { ApiClient } from './api.js';
import type { DeviceAuth } from './credentials.js';
import { DEVICE_STATE_KEYS, readDeviceState, writeDeviceState } from './device-state.js';
import type { LocalReader, SyncEngine } from './engine.js';
import { type StoredEntitlement, entitlementKeysFromJwks } from './entitlements.js';
import { type UpgradeGate, gateFromNotice } from './upload.js';

/** Warn when the station clock is off by more than this (plan §9.5). */
export const CLOCK_SKEW_WARN_MS = 2 * 60_000;

export interface ClockSkew {
  /** Server minus station, in ms (positive: the station clock is behind). */
  ms: number;
  warn: boolean;
  measuredAt: string;
}

/** Skew from one request: server time vs the midpoint of send and receive. */
export function measureClockSkew(serverTime: string, sentAt: Date, receivedAt: Date): ClockSkew {
  const midpoint = sentAt.getTime() + (receivedAt.getTime() - sentAt.getTime()) / 2;
  const ms = Math.round(Date.parse(serverTime) - midpoint);
  return { ms, warn: Math.abs(ms) > CLOCK_SKEW_WARN_MS, measuredAt: receivedAt.toISOString() };
}

/** The last config, as stored in `device_state`. */
export interface StoredDeviceConfig {
  station: DeviceConfigResponse['station'];
  device: DeviceConfigResponse['device'];
  minAppVersion: string;
  clockSkew: ClockSkew;
}

export async function readStoredConfig(db: LocalReader): Promise<StoredDeviceConfig | null> {
  return readDeviceState<StoredDeviceConfig>(db, DEVICE_STATE_KEYS.deviceConfig);
}

/**
 * Fetches `/devices/config` (and the entitlement keys) and stores what the station needs
 * offline. Raises or clears the minimum-version gate from `upgrade_required`.
 */
export async function refreshDeviceConfig(deps: {
  engine: SyncEngine;
  api: ApiClient;
  auth: DeviceAuth;
  appVersion: string;
  now: () => Date;
}): Promise<StoredDeviceConfig> {
  const { engine, api, auth, appVersion, now } = deps;
  const token = await auth.getToken();
  const { config, sentAt, receivedAt } = await api.config(token.token, now);
  const keys = await api.entitlementKeys().catch(() => null);
  const stored: StoredDeviceConfig = {
    station: config.station,
    device: config.device,
    minAppVersion: config.min_app_version,
    clockSkew: measureClockSkew(config.server_time, sentAt, receivedAt),
  };
  const entitlement: StoredEntitlement = { ...config.entitlement };
  await engine.writeTransaction(async (tx) => {
    const at = now();
    await writeDeviceState(tx, DEVICE_STATE_KEYS.deviceConfig, stored, at);
    await writeDeviceState(tx, DEVICE_STATE_KEYS.entitlement, entitlement, at);
    if (keys) {
      await writeDeviceState(
        tx,
        DEVICE_STATE_KEYS.entitlementKeys,
        entitlementKeysFromJwks(keys),
        at,
      );
    }
    // The `upgrade` object (D-050) says why; an older server only sets `upgrade_required`.
    const notice =
      upgradeNoticeFrom(config) ??
      (config.upgrade_required
        ? { reason: 'app_version' as const, min_app_version: config.min_app_version }
        : null);
    if (notice) {
      const gate = gateFromNotice(notice, appVersion, at);
      await writeDeviceState(tx, DEVICE_STATE_KEYS.upgradeGate, gate, at);
    } else {
      // The server accepts this app version now (lowered minimum, or the app was updated). A
      // schema-version gate from `/commands` stays until the app itself changes.
      const current = await readDeviceState<UpgradeGate>(tx, DEVICE_STATE_KEYS.upgradeGate);
      if (current?.reason === 'app_version') {
        await writeDeviceState(tx, DEVICE_STATE_KEYS.upgradeGate, null, at);
      }
    }
  });
  return stored;
}
